import { makeAuthenticatedRequest } from './api';

// Admin Messaging API wrappers. Backend routes are admin-gated (authMiddleware('admin')),
// POST-with-JSON-body, mounted at /messaging. See jims-mowing-api/src/routes/messaging.routes.js.

// Returns { franchisees: [{ id, fseCode, franchiseName, firstName, lastName, email, phoneNumber, isActive }] }.
export const getRecipients = async () => {
  const data = await makeAuthenticatedRequest('/messaging/recipients', {
    method: 'POST',
    body: {},
  });
  return { franchisees: data?.franchisees || [] };
};

// Sends an SMS/email to selected franchisees or broadcasts to all active franchisees.
// Returns the tally { channel, broadcast, attempted, sent, failed, skipped, results:[{ franchiseeId, fseCode, name, to, status, reason? }] }.
export const sendMessage = async ({ channel, franchiseeIds, broadcast, subject, body, overrideSmsLimit, includeName }) =>
  makeAuthenticatedRequest('/messaging/send', {
    method: 'POST',
    body: { channel, franchiseeIds, broadcast, subject, body, overrideSmsLimit, includeName },
  });

// Fetches past messages for the History tab, newest first, over a date range. Merges manual broadcasts
// (kind:'broadcast', grouped) with automated system sends (kind:'message', one per SMS/email). `from`/`to`
// are ISO strings (server-side date filter). Returns { entries:[{ id, kind, channel, automated, broadcast,
// subject, body, sentBy, to, attempted, sent, failed, skipped, outcome, recipients, testMode, timestamp }],
// truncated } — `truncated` means older rows were dropped by the server cap (narrow the date range).
export const getMessageHistory = async ({ from, to, limit } = {}) => {
  const data = await makeAuthenticatedRequest('/messaging/history', {
    method: 'POST',
    body: { from, to, limit },
  });
  return { entries: data?.entries || [], truncated: !!data?.truncated };
};

// ─── Two-way SMS conversations (ticket 71) ──────────────────────────────────────
// Threads live under /messaging/conversations/*. Inbound replies arrive via the Telnyx webhook on
// the API, so the client polls rather than subscribing (this app has no realtime Firestore usage).

// Threads, most recent first. Returns { conversations, totalUnread, truncated }.
export const getConversations = async ({ limit } = {}) => {
  const data = await makeAuthenticatedRequest('/messaging/conversations/list', {
    method: 'POST',
    body: { limit },
  });
  return {
    conversations: data?.conversations || [],
    totalUnread: data?.totalUnread || 0,
    truncated: !!data?.truncated,
  };
};

// One thread's messages, oldest first. Returns { conversation, messages, truncated }.
export const getConversationMessages = async ({ conversationId, limit } = {}) => {
  const data = await makeAuthenticatedRequest('/messaging/conversations/messages', {
    method: 'POST',
    body: { conversationId, limit },
  });
  return {
    conversation: data?.conversation || null,
    messages: data?.messages || [],
    truncated: !!data?.truncated,
  };
};

// Clear a thread's unread badge.
export const markConversationRead = (conversationId) =>
  makeAuthenticatedRequest('/messaging/conversations/read', {
    method: 'POST',
    body: { conversationId },
  });

// Send a reply. Target an existing thread (conversationId), a franchisee (franchiseeId) or a raw number.
export const sendConversationMessage = ({ conversationId, franchiseeId, phone, text }) =>
  makeAuthenticatedRequest('/messaging/conversations/send', {
    method: 'POST',
    body: { conversationId, franchiseeId, phone, text },
  });
